import type {
  FunnelGraph,
  FunnelGraphEdge,
  FunnelGraphNode,
  FunnelGraphNodeType,
  FunnelStageConfig,
  FunnelWizardState,
  Product,
} from "@/lib/funnel-planner/schema";

export type FunnelIntegrityWarningCategory =
  | "invalid_connection"
  | "dead_end"
  | "branch_split"
  | "invalid_offer_path"
  | "loop_source"
  | "wait_dead_end"
  | "missing_conversion"
  | "offer_unattached";

export type FunnelIntegritySeverity = "warning" | "critical";

export type FunnelIntegrityWarning = {
  id: string;
  category: FunnelIntegrityWarningCategory;
  severity: FunnelIntegritySeverity;
  nodeId?: string;
  edgeId?: string;
  stageId?: string;
  title: string;
  detail: string;
};

const CONVERSION_STAGE_TYPES = new Set([
  "order-form",
  "free-plus-shipping",
  "tripwire-page",
  "pricing-page",
  "free-trial",
  "application-page",
  "booking-page",
  "discovery-call",
  "proposal",
  "retainer-close",
  "cart-page",
  "in-app-upgrade",
  "activation-event",
  "community-join",
]);

const CHECKOUT_STAGE_TYPES = new Set([
  "order-form",
  "free-plus-shipping",
  "tripwire-page",
  "pricing-page",
  "cart-page",
  "sales-page",
  "in-app-upgrade",
  "application-page",
  "booking-page",
  "discovery-call",
  "proposal",
  "retainer-close",
]);

const UPSELL_STAGE_TYPES = new Set(["upsell", "downsell", "order-bump"]);

const ALLOWED_TARGETS: Record<FunnelGraphNodeType, ReadonlySet<FunnelGraphNodeType>> = {
  trafficSource: new Set(["stage", "branch", "surveySplit"]),
  stage: new Set(["stage", "branch", "wait", "surveySplit", "retargeting"]),
  branch: new Set(["stage", "wait", "surveySplit"]),
  surveySplit: new Set(["stage", "wait", "branch"]),
  wait: new Set(["stage", "branch", "surveySplit"]),
  retargeting: new Set(["stage", "wait"]),
};

function graphFromState(state: FunnelWizardState): FunnelGraph | null {
  return state.graph && state.graph.nodes.length > 0 ? state.graph : null;
}

function stageIdFromNode(node: FunnelGraphNode): string {
  return node.id.startsWith("stage-") ? node.id.slice(6) : node.id;
}

function isWaitNode(node: FunnelGraphNode): boolean {
  return node.type === "wait" || node.data.stageType === "wait-delay" || node.data.nodeKind === "wait";
}

function isTerminalStage(node: FunnelGraphNode): boolean {
  const type = node.data.stageType ?? "custom";
  return CONVERSION_STAGE_TYPES.has(type) || type === "thank-you-page";
}

function nodeLabel(node?: FunnelGraphNode): string {
  return node?.data.label || node?.id || "Unknown node";
}

function hasSourceAncestor(
  targetId: string,
  nodeById: Map<string, FunnelGraphNode>,
  incomingByTarget: Map<string, FunnelGraphEdge[]>,
  seen = new Set<string>(),
): boolean {
  if (seen.has(targetId)) return false;
  seen.add(targetId);
  const node = nodeById.get(targetId);
  if (node?.data.isSource) return true;
  const incoming = incomingByTarget.get(targetId) ?? [];
  return incoming.some((edge) => hasSourceAncestor(edge.source, nodeById, incomingByTarget, seen));
}

function edgeIsLoop(edge: FunnelGraphEdge, nodeById: Map<string, FunnelGraphNode>): boolean {
  if (edge.edgeType === "loop" || edge.edgeType === "retarget" || edge.edgeType === "retargeting") return true;
  const source = nodeById.get(edge.source);
  const target = nodeById.get(edge.target);
  return !!source && !!target && source.position.x >= target.position.x;
}

function stageById(stages: FunnelStageConfig[]): Map<string, FunnelStageConfig> {
  return new Map(stages.map((stage) => [stage.id, stage]));
}

function productStageIsAttachedToCheckout(product: Product, stages: Map<string, FunnelStageConfig>): boolean {
  const attached = product.attachedStageIds ?? [];
  return attached.some((stageId) => {
    const stage = stages.get(stageId);
    return stage ? CHECKOUT_STAGE_TYPES.has(stage.stageType) : false;
  });
}

export function validateFunnelGraph(state: FunnelWizardState): FunnelIntegrityWarning[] {
  const graph = graphFromState(state);
  if (!graph) return [];

  const warnings: FunnelIntegrityWarning[] = [];
  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
  const outgoingBySource = new Map<string, FunnelGraphEdge[]>();
  const incomingByTarget = new Map<string, FunnelGraphEdge[]>();
  for (const node of graph.nodes) {
    outgoingBySource.set(node.id, []);
    incomingByTarget.set(node.id, []);
  }
  for (const edge of graph.edges) {
    outgoingBySource.get(edge.source)?.push(edge);
    incomingByTarget.get(edge.target)?.push(edge);
  }

  for (const edge of graph.edges) {
    const source = nodeById.get(edge.source);
    const target = nodeById.get(edge.target);
    if (!source || !target) {
      warnings.push({
        id: `invalid-connection-missing-${edge.id}`,
        category: "invalid_connection",
        severity: "critical",
        edgeId: edge.id,
        title: "Connector points to a missing node",
        detail: "Remove the connector or reconnect it to an existing node.",
      });
      continue;
    }
    if (!ALLOWED_TARGETS[source.type]?.has(target.type)) {
      warnings.push({
        id: `invalid-connection-${edge.id}`,
        category: "invalid_connection",
        severity: "warning",
        edgeId: edge.id,
        title: "Connector does not match funnel node rules",
        detail: `${nodeLabel(source)} should not connect directly to ${nodeLabel(target)}.`,
      });
    }
    if (edgeIsLoop(edge, nodeById) && !hasSourceAncestor(edge.source, nodeById, incomingByTarget)) {
      warnings.push({
        id: `loop-source-${edge.id}`,
        category: "loop_source",
        severity: "warning",
        edgeId: edge.id,
        title: "Retargeting loop has no audience source",
        detail: "Attach the loop to a path that receives traffic before counting loop volume.",
      });
    }
  }

  for (const node of graph.nodes) {
    const outgoing = outgoingBySource.get(node.id) ?? [];
    if (!node.data.isSource && outgoing.length === 0 && !isTerminalStage(node)) {
      warnings.push({
        id: `dead-end-${node.id}`,
        category: isWaitNode(node) ? "wait_dead_end" : "dead_end",
        severity: isWaitNode(node) ? "critical" : "warning",
        nodeId: node.id,
        stageId: node.type === "stage" ? stageIdFromNode(node) : undefined,
        title: isWaitNode(node) ? "Wait step has no next action" : "Funnel path ends before a conversion",
        detail: `${nodeLabel(node)} needs a next action or a terminal conversion stage.`,
      });
    }
    if (outgoing.length > 1) {
      const splitEdges = outgoing.filter((edge) => (
        edge.edgeType === "branch" ||
        edge.edgeType === "split" ||
        edge.edgeType === "yes" ||
        edge.edgeType === "no" ||
        edge.edgeType === "survey" ||
        edge.edgeType === "optional"
      ));
      if (splitEdges.length > 1) {
        const totalSplit = splitEdges.reduce((sum, edge) => sum + (edge.splitPercent ?? 100), 0);
        if (Math.abs(totalSplit - 100) > 0.5) {
          warnings.push({
            id: `branch-split-${node.id}`,
            category: "branch_split",
            severity: "warning",
            nodeId: node.id,
            title: "Branch split does not total 100%",
            detail: `${nodeLabel(node)} splits add up to ${Math.round(totalSplit)}%.`,
          });
        }
      }
    }
  }

  for (const node of graph.nodes.filter((item) => item.type === "stage" && UPSELL_STAGE_TYPES.has(item.data.stageType))) {
    const incoming = incomingByTarget.get(node.id) ?? [];
    const validIncoming = incoming.some((edge) => {
      const source = nodeById.get(edge.source);
      return source ? CHECKOUT_STAGE_TYPES.has(source.data.stageType) || UPSELL_STAGE_TYPES.has(source.data.stageType) : false;
    });
    if (!validIncoming) {
      warnings.push({
        id: `invalid-offer-path-${node.id}`,
        category: "invalid_offer_path",
        severity: "warning",
        nodeId: node.id,
        stageId: stageIdFromNode(node),
        title: "Upsell or downsell is not attached to checkout",
        detail: `${nodeLabel(node)} should follow a checkout, sales, or close step.`,
      });
    }
  }

  if (!graph.nodes.some((node) => node.type === "stage" && CONVERSION_STAGE_TYPES.has(node.data.stageType))) {
    warnings.push({
      id: "missing-conversion-stage",
      category: "missing_conversion",
      severity: "critical",
      title: "No purchase or conversion stage is modeled",
      detail: "Add a checkout, booking, application, upgrade, or close stage before using revenue projections.",
    });
  }

  const stagesById = stageById(state.stages);
  for (const product of state.products) {
    if (!productStageIsAttachedToCheckout(product, stagesById)) {
      warnings.push({
        id: `offer-unattached-${product.id}`,
        category: "offer_unattached",
        severity: "warning",
        title: "Offer is not attached to a sales path",
        detail: "Attach each offer to a checkout, booking, proposal, or close stage.",
      });
    }
  }

  return warnings.slice(0, 20);
}

export function summarizeIntegrityWarnings(warnings: FunnelIntegrityWarning[]): Record<FunnelIntegrityWarningCategory, number> {
  return warnings.reduce((summary, warning) => {
    summary[warning.category] = (summary[warning.category] ?? 0) + 1;
    return summary;
  }, {} as Record<FunnelIntegrityWarningCategory, number>);
}
